import React, { useEffect, useRef, useState } from 'react';
import { Modal, Slider, Typography, message } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import './Personel.css';

const { Text } = Typography;

// Personel fotoğrafı kırpma (03.11.2026). Kayıtlı fotoğraflar 4:5 dikey; avatar daire olduğu için ortadaki kare
// görünür, çerçevedeki daire o alanı gösterir. Çıktı 480×600 JPEG — sunucu (services/personel_foto.py) yine
// doğrulayıp yeniden kodlar.
const CERCEVE_G = 280;
const CERCEVE_Y = 350;
const CIKTI_G = 480;
const CIKTI_Y = 600;

const PersonelFotoKirp = ({ dosya, kaydediliyor, onKaydet, onIptal }) => {
    const [kaynak, setKaynak] = useState(null);
    const [boyut, setBoyut] = useState(null);
    const [yakinlik, setYakinlik] = useState(1);
    const [konum, setKonum] = useState({ x: 0, y: 0 });
    const resimRef = useRef(null);
    const surukle = useRef(null);

    useEffect(() => {
        if (!dosya) return undefined;
        const url = URL.createObjectURL(dosya);
        setKaynak(url);
        setBoyut(null);
        setYakinlik(1);
        return () => URL.revokeObjectURL(url);
    }, [dosya]);

    const tabanOlcek = boyut ? Math.max(CERCEVE_G / boyut.g, CERCEVE_Y / boyut.y) : 1;
    const olcek = tabanOlcek * yakinlik;

    const sinirla = (x, y, o = olcek) => boyut ? {
        x: Math.min(0, Math.max(CERCEVE_G - boyut.g * o, x)),
        y: Math.min(0, Math.max(CERCEVE_Y - boyut.y * o, y))
    } : { x, y };

    const resimYuklendi = (e) => {
        const g = e.target.naturalWidth;
        const y = e.target.naturalHeight;
        const o = Math.max(CERCEVE_G / g, CERCEVE_Y / y);
        setBoyut({ g, y });
        // Yüz çoğunlukla üst yarıda: dikey fotoğrafta üstten biraz pay bırakarak başla
        setKonum({ x: (CERCEVE_G - g * o) / 2, y: (CERCEVE_Y - y * o) * 0.3 });
    };

    const yakinlikDegisti = (yeni) => {
        // Çerçevenin ortası sabit kalsın
        const yeniOlcek = tabanOlcek * yeni;
        const mx = (CERCEVE_G / 2 - konum.x) / olcek;
        const my = (CERCEVE_Y / 2 - konum.y) / olcek;
        setYakinlik(yeni);
        setKonum(sinirla(CERCEVE_G / 2 - mx * yeniOlcek, CERCEVE_Y / 2 - my * yeniOlcek, yeniOlcek));
    };

    const basla = (e) => {
        if (!boyut) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        surukle.current = { px: e.clientX, py: e.clientY, x: konum.x, y: konum.y };
    };
    const tasi = (e) => {
        const s = surukle.current;
        if (!s) return;
        setKonum(sinirla(s.x + e.clientX - s.px, s.y + e.clientY - s.py));
    };
    const birak = () => { surukle.current = null; };

    const kaydet = () => {
        const tuval = document.createElement('canvas');
        tuval.width = CIKTI_G;
        tuval.height = CIKTI_Y;
        const ctx = tuval.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CIKTI_G, CIKTI_Y);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(resimRef.current, -konum.x / olcek, -konum.y / olcek, CERCEVE_G / olcek, CERCEVE_Y / olcek,
            0, 0, CIKTI_G, CIKTI_Y);
        tuval.toBlob((blob) => blob && onKaydet(blob), 'image/jpeg', 0.92);
    };

    return (
        <Modal
            open={!!dosya}
            title="Fotoğrafı Yerleştir"
            okText="Kaydet"
            cancelText="Vazgeç"
            onOk={kaydet}
            onCancel={onIptal}
            okButtonProps={{ disabled: !boyut, loading: kaydediliyor }}
            cancelButtonProps={{ disabled: kaydediliyor }}
            closable={!kaydediliyor}
            maskClosable={false}
            width={380}
            centered
        >
            <div className="pf-kirp">
                <div
                    className="pf-cerceve"
                    style={{ width: CERCEVE_G, height: CERCEVE_Y }}
                    onPointerDown={basla}
                    onPointerMove={tasi}
                    onPointerUp={birak}
                    onPointerCancel={birak}
                >
                    {kaynak && (
                        <img
                            ref={resimRef}
                            src={kaynak}
                            alt=""
                            draggable={false}
                            onLoad={resimYuklendi}
                            style={boyut ? {
                                width: boyut.g * olcek,
                                height: boyut.y * olcek,
                                transform: `translate(${konum.x}px, ${konum.y}px)`
                            } : { visibility: 'hidden' }}
                        />
                    )}
                    <div className="pf-daire" style={{ top: (CERCEVE_Y - CERCEVE_G) / 2, width: CERCEVE_G, height: CERCEVE_G }} />
                </div>
                <div className="pf-yakinlik">
                    <ZoomOutOutlined />
                    <Slider min={1} max={4} step={0.01} value={yakinlik} onChange={yakinlikDegisti}
                        tooltip={{ open: false }} disabled={!boyut} />
                    <ZoomInOutlined />
                </div>
                <Text type="secondary" className="pf-ipucu">
                    Sürükleyerek yerleştirin. Dairenin içi profil ve menülerde görünür.
                </Text>
            </div>
        </Modal>
    );
};

// Seç → kırp → yükle akışı; İK › Personel ve Personel Listesi ortak kullanır. Yükleyebilenler yalnız İK + BT
// pozisyonları (backend/services/personel_foto.py); düğmeyi `yetkili` ise gösterin, `pencere`'yi sayfaya koyun.
export const usePersonelFoto = (onYuklendi) => {
    const { getAuthHeaders } = useAuth();
    const [yetkili, setYetkili] = useState(false);
    const [hedef, setHedef] = useState(null);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const girdiRef = useRef(null);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/personel-foto/yetki`, { headers: getAuthHeaders() })
            .then((r) => setYetkili(!!r.data?.yetkili))
            .catch(() => setYetkili(false));
        return () => girdiRef.current?.remove();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sec = (kullaniciNo) => {
        if (!girdiRef.current) {
            const girdi = document.createElement('input');
            girdi.type = 'file';
            girdi.accept = 'image/jpeg,image/png,image/webp';
            girdi.style.display = 'none';
            document.body.appendChild(girdi);
            girdiRef.current = girdi;
        }
        const girdi = girdiRef.current;
        girdi.value = '';
        girdi.onchange = () => {
            const dosya = girdi.files?.[0];
            if (!dosya) return;
            if (!/^image\/(jpeg|png|webp)$/.test(dosya.type)) {
                message.error('Fotoğraf JPG, PNG ya da WEBP olmalı.');
            } else if (dosya.size > 15 * 1024 * 1024) {
                message.error('Fotoğraf en fazla 15 MB olabilir.');
            } else {
                setHedef({ kullaniciNo, dosya });
            }
        };
        girdi.click();
    };

    const kaydet = async (blob) => {
        const veri = new FormData();
        veri.append('foto', blob, `${hedef.kullaniciNo}.jpg`);
        try {
            setKaydediliyor(true);
            // getAuthHeaders() 'Content-Type: application/json' de verir; axios o zaman FormData'yı JSON'a çevirir
            await axios.post(`${API_BASE_URL}/personel-foto/${hedef.kullaniciNo}`, veri, {
                headers: { Authorization: getAuthHeaders().Authorization }
            });
            message.success('Fotoğraf güncellendi.');
            onYuklendi?.(hedef.kullaniciNo);
            setHedef(null);
        } catch (error) {
            message.error(error.response?.data?.detail || 'Fotoğraf yüklenemedi.');
        } finally {
            setKaydediliyor(false);
        }
    };

    const pencere = (
        <PersonelFotoKirp
            dosya={hedef?.dosya}
            kaydediliyor={kaydediliyor}
            onKaydet={kaydet}
            onIptal={() => setHedef(null)}
        />
    );
    return { yetkili, sec, pencere };
};

export default PersonelFotoKirp;
